import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Text,
  Spinner,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Delete16Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import {
  DiscussionAttachmentDto,
  getDiscussionAttachments,
  uploadDiscussionAttachment,
  deleteDiscussionAttachment,
  downloadDiscussionAttachment,
} from "../../../services/discussion-attachments.service";
import { useTypographyStyles } from "../../../styles/typography.styles";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { validateAttachmentFile } from "../attachmentConstraints";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  // Drag-over gets a dashed brand-coloured outline so users see the drop zone.
  // Otherwise sits flush with the container so it doesn't add visual weight.
  dropZone: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    minHeight: "88px",
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    alignItems: "flex-start",
  },
  dropZoneActive: {
    // Re-declare the whole `border` shorthand — Griffel disallows
    // shorthand-in-one-rule + longhand-in-another because it can't guarantee
    // the merge order at runtime.
    border: `1px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  thumb: {
    position: "relative",
    width: "96px",
    height: "96px",
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  thumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  deleteButton: {
    position: "absolute",
    top: "2px",
    right: "2px",
    minWidth: "24px",
    width: "24px",
    height: "24px",
    padding: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  // Lightbox — taskpane is narrow, so let the dialog take up to the full
  // pane width and use the image's natural aspect ratio.
  lightboxSurface: {
    maxWidth: "95vw",
    width: "auto",
    padding: 0,
  },
  lightboxBody: {
    // Center the image — Fluent's DialogSurface has an internal min-width,
    // so a tall/thin image (narrower than that min) would sit flush left with
    // dead space to the right. Flex-centre keeps it in the middle regardless.
    padding: 0,
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // Clip the zoomed/panned image to the dialog bounds so it doesn't spill
    // out over neighbouring surfaces when the user drags it around.
    overflow: "hidden",
  },
  lightboxImage: {
    display: "block",
    maxWidth: "95vw",
    maxHeight: "85vh",
    objectFit: "contain",
    // Chrome/Edge fire native drag on images by default (ghost image + drop
    // side effects); block selection via CSS and pair with draggable={false}
    // in JSX to stop the native drag gesture entirely so our pan handlers
    // own it.
    userSelect: "none",
  },
  lightboxHint: {
    position: "absolute",
    left: tokens.spacingHorizontalS,
    bottom: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    pointerEvents: "none",
  },
  lightboxClose: {
    position: "absolute",
    top: tokens.spacingHorizontalS,
    right: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface Props {
  discussionId: number;
  /**
   * Files the parent has picked (via its own <input type="file"> living in
   * DiscussionThread). Setting a non-empty array triggers an upload; the
   * component calls onFilesConsumed once the array has been picked up so the
   * parent can reset the state for the next selection.
   */
  pendingFiles?: File[];
  onFilesConsumed?: () => void;
  /**
   * Extra class merged onto the panel's root. Exists so the parent can
   * position the panel (DiscussionThread pins it to the bottom of the
   * scroll area) without wrapping it in a div — a wrapper would survive
   * this component's render-nothing-when-empty path and leave a stray
   * flex child plus its gap behind.
   */
  className?: string;
}

const DiscussionAttachments: React.FC<Props> = ({
  discussionId,
  pendingFiles,
  onFilesConsumed,
  className,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { userInfo } = useWorkflowIntelligence();
  const currentUserEmail = userInfo?.mail?.toLowerCase() ?? "";

  const [attachments, setAttachments] = useState<DiscussionAttachmentDto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Maps attachmentId → blob URL so re-opening the lightbox for the same
  // image doesn't refetch the bytes. The unmount effect revokes every URL.
  const objectUrlCacheRef = useRef<Map<number, string>>(new Map());
  // In-flight image srcs so <img> tags refresh when a fresh blob URL arrives.
  const [thumbSrcs, setThumbSrcs] = useState<Record<number, string>>({});
  // Split "which image" from "is the dialog open" so the <img> stays mounted
  // during Fluent Dialog's exit animation. If we cleared the URL on close,
  // the image would disappear while the dialog frame is still shrinking —
  // reading as an empty shrinking bar for the ~200ms fade-out.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Attachment queued for delete-confirm. window.confirm silently returns
  // false in Excel's WebView2 host (broke this once already), so we route
  // the "Are you sure?" through a Fluent Dialog instead.
  const [pendingDelete, setPendingDelete] = useState<DiscussionAttachmentDto | null>(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  // Lightbox zoom/pan. The add-in pane is narrow, so users need to zoom in
  // to read screenshot detail. Reset happens in openAttachment when a new
  // image loads so opening a second attachment doesn't inherit the previous
  // one's zoom/pan.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number }>({
    x: 0, y: 0, panX: 0, panY: 0,
  });

  const load = useCallback(async () => {
    try {
      const list = await getDiscussionAttachments(discussionId);
      setAttachments(list);
    } catch (err) {
      // Swallow initial-load failures — worst case the panel stays hidden.
      // Upload / delete errors DO surface via the inline error banner below.
      console.error("Failed to load discussion attachments", err);
    }
  }, [discussionId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Revoke object URLs on unmount so we don't leak blobs when the user
  // switches to another discussion.
  useEffect(() => {
    const cache = objectUrlCacheRef.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  // Fetch bytes for any new image attachments so their thumbnails render.
  useEffect(() => {
    const imageAttachments = attachments.filter((a) => a.contentType.startsWith("image/"));
    let cancelled = false;
    (async () => {
      for (const a of imageAttachments) {
        if (objectUrlCacheRef.current.has(a.id)) continue;
        try {
          const blob = await downloadDiscussionAttachment(discussionId, a.id);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          objectUrlCacheRef.current.set(a.id, url);
          setThumbSrcs((prev) => ({ ...prev, [a.id]: url }));
        } catch {
          // Fail silently per-thumbnail — the thumb just stays blank.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachments, discussionId]);

  const validateAndUpload = useCallback(async (files: File[]) => {
    setError(null);
    for (const file of files) {
      const reason = validateAttachmentFile(file);
      if (reason) {
        setError(reason);
        continue;
      }
      setUploading(true);
      try {
        const created = await uploadDiscussionAttachment(discussionId, file);
        setAttachments((prev) => [...prev, created]);
      } catch (err) {
        setError(getUserFriendlyErrorMessage(err));
      } finally {
        setUploading(false);
      }
    }
  }, [discussionId]);

  // Pick up files the parent has selected via its own <input type="file">.
  // The parent clears its own state after we call onFilesConsumed so the same
  // file can be selected twice in a row.
  useEffect(() => {
    if (pendingFiles && pendingFiles.length > 0) {
      void validateAndUpload(pendingFiles);
      onFilesConsumed?.();
    }
  }, [pendingFiles, validateAndUpload, onFilesConsumed]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) void validateAndUpload(files);
  };


  // Click → open in the in-page lightbox. Only images are allowed as
  // attachments (see ALLOWED_CONTENT_TYPES + the backend allowlist), so
  // every attachment can safely render as an <img>. Blob URLs are cached
  // so re-opening the same image doesn't re-download the bytes.
  const openAttachment = async (a: DiscussionAttachmentDto) => {
    try {
      let url = objectUrlCacheRef.current.get(a.id);
      if (!url) {
        const blob = await downloadDiscussionAttachment(discussionId, a.id);
        url = URL.createObjectURL(blob);
        objectUrlCacheRef.current.set(a.id, url);
      }
      setLightboxUrl(url);
      setLightboxOpen(true);
      // Reset zoom/pan so opening a second attachment doesn't inherit the
      // first one's state.
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err));
    }
  };

  // Zoom on the lightbox image. Scroll up = zoom in, scroll down = zoom out.
  // Clamped to [1, 4] — 1x is "fit to dialog," anything below that just adds
  // dead space; 4x covers "read a specific cell value in a screenshot" without
  // becoming a lossy pixel-inspector.
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.2;
  const handleLightboxWheel = (e: React.WheelEvent<HTMLImageElement>) => {
    const next = e.deltaY < 0
      ? Math.min(ZOOM_MAX, zoom + ZOOM_STEP)
      : Math.max(ZOOM_MIN, zoom - ZOOM_STEP);
    setZoom(next);
    // At 1x the image should be centered — no residual pan from a previous
    // zoom-in-then-zoom-back session.
    if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
  };

  const handleLightboxMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom <= ZOOM_MIN) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };

  const handleLightboxMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isDragging) return;
    setPan({
      x: dragStartRef.current.panX + (e.clientX - dragStartRef.current.x),
      y: dragStartRef.current.panY + (e.clientY - dragStartRef.current.y),
    });
  };

  const handleLightboxMouseUp = () => {
    if (isDragging) setIsDragging(false);
  };

  // Delete flow is split so the button-click just queues the confirmation
  // (setPendingDelete), and the actual API call fires from the dialog's
  // Confirm button (confirmDelete). The e.stopPropagation on the button
  // prevents its click from bubbling to the thumb and opening the lightbox
  // as well.
  const requestDelete = (a: DiscussionAttachmentDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDelete(a);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const a = pendingDelete;
    setDeleteInFlight(true);
    try {
      await deleteDiscussionAttachment(discussionId, a.id);
      const cachedUrl = objectUrlCacheRef.current.get(a.id);
      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
        objectUrlCacheRef.current.delete(a.id);
      }
      setThumbSrcs((prev) => {
        const next = { ...prev };
        delete next[a.id];
        return next;
      });
      setAttachments((prev) => prev.filter((x) => x.id !== a.id));
      setPendingDelete(null);
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setDeleteInFlight(false);
    }
  };

  const canDelete = (a: DiscussionAttachmentDto) =>
    (a.uploadedByEmail ?? "").toLowerCase() === currentUserEmail;

  // Nothing to show — no thumbs, no in-flight upload, no upload/delete error.
  // Load failures don't count (see load()'s catch), so the panel stays gone
  // until we've confirmed there IS something to render.
  if (!uploading && error === null && attachments.length === 0) {
    return null;
  }

  return (
    <div className={className ? `${styles.container} ${className}` : styles.container}>
      {uploading && <Spinner size="tiny" label="Uploading..." />}

      {error && (
        <div className={styles.errorMessage}>
          <Text className={typography.errorText}>{error}</Text>
        </div>
      )}

      <div
        className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {attachments.map((a) => {
            const src = thumbSrcs[a.id];
            return (
              <Tooltip
                key={a.id}
                relationship="label"
                content={`${a.fileName} (${(a.fileSize / 1024).toFixed(0)} KB)`}
              >
                <div className={styles.thumb} onClick={() => void openAttachment(a)}>
                  {src ? (
                    <img src={src} alt={a.fileName} className={styles.thumbImage} />
                  ) : (
                    <Spinner size="tiny" />
                  )}
                  {canDelete(a) && (
                    <Button
                      appearance="subtle"
                      className={styles.deleteButton}
                      icon={<Delete16Regular />}
                      onClick={(e) => requestDelete(a, e)}
                      aria-label={`Delete ${a.fileName}`}
                    />
                  )}
                </div>
              </Tooltip>
            );
        })}
      </div>

      <Dialog
        open={lightboxOpen}
        onOpenChange={(_, data) => setLightboxOpen(data.open)}
      >
        <DialogSurface className={styles.lightboxSurface}>
          <DialogBody className={styles.lightboxBody}>
            {lightboxUrl && (
              <img
                src={lightboxUrl}
                alt="Attachment preview"
                className={styles.lightboxImage}
                draggable={false}
                onWheel={handleLightboxWheel}
                onMouseDown={handleLightboxMouseDown}
                onMouseMove={handleLightboxMouseMove}
                onMouseUp={handleLightboxMouseUp}
                onMouseLeave={handleLightboxMouseUp}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  // Skip transition mid-drag so panning tracks the cursor 1:1;
                  // let zoom steps animate lightly for a smoother feel.
                  transition: isDragging ? "none" : "transform 0.1s ease-out",
                  cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                }}
              />
            )}
            <div className={styles.lightboxHint}>
              {zoom > 1
                ? `${Math.round(zoom * 100)}% · Drag to pan`
                : "Scroll to zoom"}
            </div>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              className={styles.lightboxClose}
              onClick={() => setLightboxOpen(false)}
              aria-label="Close preview"
            />
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(_, data) => {
          // Ignore dismiss requests while the delete is in flight so the
          // dialog doesn't disappear mid-API-call.
          if (deleteInFlight) return;
          if (!data.open) setPendingDelete(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete attachment?</DialogTitle>
            <DialogContent>
              {pendingDelete && (
                <Text>
                  &quot;{pendingDelete.fileName}&quot; will be removed from this discussion. This can&apos;t be undone.
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingDelete(null)} disabled={deleteInFlight}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void confirmDelete()} disabled={deleteInFlight}>
                {deleteInFlight ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DiscussionAttachments;
